import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Save, ChevronLeft, ClipboardList, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";

// Sections use SMIFS letter codes from the checklist brief. Sections A/E/G/J are intentionally
// left unmapped by default — HO can still assign a course to them here if they want to.
const SECTION_LIST = [
    { code: "A", title: "Arrival & site opening" },
    { code: "B", title: "Receptionist" },
    { code: "C", title: "Housekeeping" },
    { code: "D", title: "Security Guard" },
    { code: "E", title: "Client-facing service delivery" },
    { code: "F", title: "Consumables, daily check" },
    { code: "G", title: "Consumables, weekly reorder" },
    { code: "H", title: "Other contracted services" },
    { code: "J", title: "End-of-day close" },
];

const ROLE_OPTIONS = ["HOUSEKEEPER", "SECURITY", "RECEPTIONIST", "OTHER"];

export default function CoachingMappingPage() {
    const [courses, setCourses] = useState([]);
    const [map, setMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [c, m] = await Promise.all([api.get("/courses"), api.get("/coaching/mapping")]);
            setCourses(c.data.filter((cc) => cc.published !== false));
            setMap(m.data.map || {});
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    function update(section, patch) {
        setMap((m) => {
            const next = { ...m };
            const entry = { ...(next[section] || {}), ...patch };
            if (!entry.course_id && !entry.role) delete next[section]; else next[section] = entry;
            return next;
        });
    }

    async function save() {
        // Validate: any partial entries?
        const bad = Object.entries(map).filter(([, e]) => (e.course_id && !e.role) || (!e.course_id && e.role));
        if (bad.length) { toast.error(`Each mapped section needs both a course and a role (missing: ${bad.map(([k]) => k).join(", ")})`); return; }
        setSaving(true);
        try {
            const payload = { map: Object.fromEntries(Object.entries(map).filter(([, e]) => e.course_id && e.role).map(([k, e]) => [k, { course_id: e.course_id, role: e.role }])) };
            const { data } = await api.put("/coaching/mapping", payload);
            setMap(data.map || {});
            toast.success("Mapping saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    }

    if (loading) return <div className="mx-auto max-w-4xl p-6"><div className="h-64 rounded-xl bg-white animate-pulse" /></div>;

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to="/coaching" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3355] mb-3" data-testid="mapping-back-link"><ChevronLeft className="w-4 h-4" /> Coaching queue</Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">HO Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2"><LinkIcon className="w-6 h-6" /> Section → course mapping</h1>
                    <p className="text-sm text-gray-500 mt-1">When a checklist section shows a weakness (3+ misses in 14 days), the mapped course is auto-assigned to the responsible role at that site.</p>
                </div>
                <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60" data-testid="mapping-save">
                    <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save mapping"}
                </button>
            </div>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                <table className="w-full text-sm" data-testid="mapping-table">
                    <thead className="bg-[#F4F6F9] text-left text-[11px] font-bold uppercase tracking-widest text-gray-500">
                        <tr>
                            <th className="px-4 py-3 w-12">Code</th>
                            <th className="px-4 py-3">Section</th>
                            <th className="px-4 py-3">Course</th>
                            <th className="px-4 py-3 w-40">Responsible role</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {SECTION_LIST.map((s) => {
                            const entry = map[s.code] || {};
                            return (
                                <tr key={s.code} className="hover:bg-[#FAFBFC]" data-testid={`mapping-row-${s.code}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-[#1E3355] font-bold">{s.code}</td>
                                    <td className="px-4 py-3 text-gray-800">{s.title}</td>
                                    <td className="px-4 py-3">
                                        <select value={entry.course_id || ""} onChange={(e) => update(s.code, { course_id: e.target.value })} className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs" data-testid={`mapping-course-${s.code}`}>
                                            <option value="">— none —</option>
                                            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3">
                                        <select value={entry.role || ""} onChange={(e) => update(s.code, { role: e.target.value })} className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs" data-testid={`mapping-role-${s.code}`}>
                                            <option value="">— none —</option>
                                            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                                        </select>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 text-[11px] text-gray-500 italic">
                Sections A / E / G / J are left unmapped by default. Set a course + role for any section you want the auto-coaching engine to react to.
            </div>
        </div>
    );
}
